import { useEffect, useMemo, useRef, useState } from 'react';
import { useNoticeToast } from '../lib/toast';
import { useFitHeight } from '../lib/fitHeight';
import { TopScrollbar } from '../components/TopScrollbar';
import { api, fetchDriverOrderFileUrl, uploadAcctDriverFile, type AcctCorp, type DriverOrderFinResult, type DriverOrderImportTab, type DriverOrderKind, type DriverOrderRow, type DriverOrderStatus } from '../lib/api';
import { ErrorBox, ResizeHandle, dash, money } from '../components/DataTable';
import Modal from '../components/Modal';
import { DriverNoticePanel } from './DriverNotice';
import { DriverSyncLine } from './DriverInvoiceImport';
import { ProxyRequestForm } from './DriverProxy';
import { canScreen, useMe } from '../lib/me';
import { useSticky, useAcctCorp } from '../lib/sticky';
import { SortTh, useSort, useSortedRows } from '../lib/sort';
import { colWidthStyle, useTablePrefs } from '../lib/tableCols';
import { armCopy } from '../lib/cellCopy';
import { ROW_CHECKED_CLASS, useRowChecks } from '../lib/rowChecks';

/**
 * 기사개인오더 ④ — 페이앱·카드단말기·현금영수증·다수배분(회계팀). 기획 `plan/driver-orders.md` ④.
 * dytaxdriver 탭 20개를 전량 옮겼다(2026-09-25) — 여기가 정본이다. 처리자를 적으면 처리완료, 입금일자를 적으면 입금완료.
 * 기사가 아직 기사접수 앱(시트)으로 신청하는 동안만 「시트 새 신청 가져오기」로 새 번호를 들여온다.
 */

const kst = () => new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const PAGE = 200;
const KINDS: DriverOrderKind[] = ['페이앱', '카드단말기', '현금영수증', '다수배분'];
const KIND_STYLE: Record<DriverOrderKind, string> = {
  페이앱: 'bg-violet-50 text-violet-700', 카드단말기: 'bg-emerald-50 text-emerald-700', 현금영수증: 'bg-amber-50 text-amber-800', 다수배분: 'bg-red-50 text-red-700',
};

function Status({ r }: { r: DriverOrderRow }) {
  if (r.cancel_state) return <span className="rounded bg-red-50 px-1.5 text-xs text-red-700">{r.cancel_state}</span>;
  if (r.paid_date) return <span className="rounded bg-emerald-50 px-1.5 text-xs text-emerald-700">입금완료</span>;
  if (r.issuer) return <span className="rounded bg-blue-50 px-1.5 text-xs text-blue-700">처리완료</span>;
  return <span className="rounded bg-canvas px-1.5 text-xs text-ink-soft">처리 전</span>;
}

const HEADS = [['확인', undefined], ['상태', undefined], ['종류', 'kind'], ['번호', 'order_no'], ['법인', 'corp_name'], ['신청자', 'requester'], ['거래처(배분)', 'cust_name'],
  ['요청일자', 'request_date'], ['금액', 'amount'], ['수수료', undefined], ['연락처', 'phone'], ['첨부', undefined], ['비고', 'memo'], ['처리자', 'issuer'], ['입금일자', 'paid_date']] as const;

/** 다수배분은 거래처 칸이 비고 배분 줄에 받는 사람이 있다 — 목록에는 앞 두 명만. */
const distSummary = (r: DriverOrderRow) => {
  const live = r.lines.filter((l) => !l.cancelled && l.dist_type !== '배분없음' && l.dist_name);
  if (!live.length) return null;
  return live.slice(0, 2).map((l) => l.dist_name!.split('|')[0]!.trim()).join(', ') + (live.length > 2 ? ` 외 ${live.length - 2}` : '');
};

export function DriverOrdersTab({ corps }: { corps: AcctCorp[] }) {
  const fitRef = useRef<HTMLDivElement>(null);
  const fitH = useFitHeight(fitRef);
  const [year, setYear] = useState(Number(kst().slice(0, 4)));
  const [kind, setKind] = useSticky<DriverOrderKind | ''>('acct.dorder.kind', '');
  const [corpId, setCorpId] = useAcctCorp();
  const [status, setStatus] = useSticky<DriverOrderStatus>('acct.dorder.status', 'all');
  const [term, setTerm] = useSticky('acct.dorder.term', '');
  const [q, setQ] = useSticky('acct.dorder.q', '');
  const [nonce, setNonce] = useState(0);
  const [data, setData] = useState<Awaited<ReturnType<typeof api.listDriverOrders>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<DriverOrderRow | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(0);
  // #206 회계팀이 기사 대신 등록(기사 앱과 같은 번호·신청자)
  const [proxyOpen, setProxyOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  useNoticeToast(notice, setNotice);
  const { me } = useMe();
  const canWrite = canScreen(me, 'accounting', 'write');

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.listDriverOrders({ year, kind: kind || null, corpId, status, q: q || undefined })
      .then((r) => { if (!cancelled) { setData(r); setPage(0); } })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, kind, corpId, status, q, nonce]);
  const reload = () => setNonce((n) => n + 1);
  async function openAttach(id: number) {
    try { const url = await fetchDriverOrderFileUrl(id); window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60_000); }
    catch (e) { setError(errMsg(e)); }
  }
  const grand = data?.totals.find((t) => t.kind === null);
  // 머리글 정렬(#37) — 목록을 전부 받아 두니 화면이 정렬한다(쪽 나누기 전에).
  const [sort, toggleSort] = useSort('acct.dorder');
  const onSort = (k: string) => { toggleSort(k); setPage(0); };
  // #208 ② 입금일자 하루만 보기(받아 둔 목록에서 거른다 — 해 단위로 다 받아 둔다).
  const sorted = useSortedRows(data?.rows, sort);
  const [paidOn, setPaidOn] = useState('');
  const shown = useMemo(() => (paidOn ? sorted.filter((r) => r.paid_date === paidOn) : sorted), [sorted, paidOn]);
  const total = shown.length;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  // #208 ① 열 너비(엑셀 열 너비처럼) — 머리글 오른쪽 끝을 끌어 조절 · 이 브라우저에 기억(다른 표와 같은 방식).
  const { prefs, setWidth, reset: resetCols, customized } = useTablePrefs('acct.dorder');
  const anyWidth = Object.keys(prefs.width).length > 0;
  // #208 ③ 누르고 Ctrl+C = 그 칸 복사(#250) · 두 번 누름 = 고치기 창(출납일보와 같은 손버릇).
  const copyCell = (e: React.MouseEvent) => {
    const td = (e.target as HTMLElement).closest('td');
    const text = td?.innerText.trim();
    if (!td || !text || text === '—' || td.dataset.nocopy) return;
    // #250 — 누르면 고르기만, Ctrl+C 로 복사.
    armCopy(text, td); // 화면 아래 「<칸> 복사: …」(#446 공용)
  };

  // #382 확인 체크 — 재무추가(#142)와 같은 방식: 체크한 줄은 초록, 이 브라우저에만 기억(본인만 보인다).
  const { isChecked, toggle: toggleCheck } = useRowChecks('businweb.driverOrders.checked');
  const body = useMemo(() => shown.slice(page * PAGE, (page + 1) * PAGE).map((r) => (
    <tr key={r.order_id} onClick={copyCell} onDoubleClick={() => setEditing(r)} title="누르고 Ctrl+C 로 그 칸 복사 · 두 번 누르면 고치기"
        className={`cursor-pointer border-b ${isChecked(r.order_id) ? ROW_CHECKED_CLASS : 'hover:bg-brand-50/40'} ${r.cancel_state ? 'text-ink-faint line-through decoration-ink-faint/40' : ''}`}>
      <td className="px-2 text-center" data-nocopy="1" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
        <input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={isChecked(r.order_id)} onChange={() => toggleCheck(r.order_id)} /></td>
      <td className="whitespace-nowrap px-2 py-1.5"><Status r={r} />
        {r.from_app && <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] text-amber-800">앱</span>}
        {r.by_acct && <span className="ml-1 rounded bg-orange-50 px-1 text-[10px] text-orange-700" title={`회계팀이 기사 대신 등록(${r.by ?? ''})`}>대리</span>}
        {r.by_driver && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800" title={`${r.by_driver} 기사가 대신 신청`}>대신</span>}
        {r.notified_at && <span className="ml-1 rounded bg-sky-50 px-1 text-[10px] text-sky-700" title={`발행 알림톡 보냄 ${r.notified_at}`}>알림</span>}</td>
      <td className="whitespace-nowrap px-2"><span className={`rounded px-1.5 text-xs ${KIND_STYLE[r.kind]}`}>{r.kind}</span></td>
      <td className="whitespace-nowrap px-2 font-mono">{r.order_no}</td>
      <td className="whitespace-nowrap px-2">{r.corp_name}</td>
      <td className="whitespace-nowrap px-2">{dash(r.requester)}</td>
      <td className="max-w-[14rem] truncate px-2" title={r.cust_name ?? ''}>{r.cust_name ?? <span className="text-ink-faint">{distSummary(r) ?? '—'}</span>}</td>
      <td className="whitespace-nowrap px-2">{dash(r.request_date)}</td>
      <td className="px-2 text-right font-medium">{money(r.amount)}</td>
      <td className="px-2 text-right text-ink-soft" title={r.deposit_amount ? `입금 ${money(r.deposit_amount)}원` : '입금금액을 넣으면 보입니다'}>
        {r.deposit_amount ? money(Number(r.amount) - Number(r.deposit_amount)) : ''}</td>
      <td className="whitespace-nowrap px-2">{dash(r.phone)}</td>
      <td className="px-2" data-nocopy="1" onClick={(e) => e.stopPropagation()}>
        {r.attach_url ? <a className="text-brand-700 underline" href={r.attach_url} target="_blank" rel="noopener noreferrer">보기</a>
          : r.attach_name ? <button className="text-xs" title={`첨부 보기: ${r.attach_name}`} onClick={() => void openAttach(Number(r.order_id))}>📎</button> : ''}</td>
      <td className="max-w-[14rem] truncate px-2" title={r.memo ?? ''}>{dash(r.memo)}</td>
      <td className="px-2">{dash(r.issuer)}</td>
      <td className="whitespace-nowrap px-2">{dash(r.paid_date)}</td>
    </tr>
  )), [shown, page, isChecked, toggleCheck]);

  return (
    <div className="space-y-1.5">
      <div className="btn-row-sm flex flex-wrap items-center gap-2">
        <select className="field w-24" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[0, 1, 2].map((d) => { const y = Number(kst().slice(0, 4)) - d; return <option key={y} value={y}>{y}년</option>; })}
        </select>
        <select className="field w-32" value={kind} onChange={(e) => setKind(e.target.value as DriverOrderKind | '')}>
          <option value="">4종 전체</option>{KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <select className="field w-36" value={corpId ?? ''} onChange={(e) => setCorpId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">전체 법인</option>
          {corps.filter((c) => c.invoice_code).map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}</option>)}
        </select>
        <select className="field w-32" value={status} onChange={(e) => setStatus(e.target.value as DriverOrderStatus)}>
          <option value="all">취소 포함 전체</option><option value="active">취소 뺀 전체</option><option value="pending">처리 전</option><option value="done">처리완료·미입금</option>
          <option value="paid">입금완료</option><option value="cancelled">취소</option>
        </select>
        <form onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); }} className="flex items-center gap-2">
          <input className="field w-56" placeholder="번호·기사·거래처·사업자·비고" value={term} onChange={(e) => setTerm(e.target.value)} />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </form>
        <label className="flex items-center gap-1 text-sm text-ink-soft" title="#208 — 그 날 입금된 것만">
          입금일자
          <input type="date" className="field w-36" value={paidOn} onChange={(e) => { setPaidOn(e.target.value); setPage(0); }} />
          {paidOn && <button type="button" className="text-xs underline" onClick={() => { setPaidOn(''); setPage(0); }}>전체</button>}
        </label>
        {customized && <button type="button" className="btn-ghost text-xs" onClick={resetCols} title="#208 — 끌어서 바꾼 열 너비를 처음대로">열 너비 처음대로</button>}
        <div className="ml-auto flex gap-2">
          {!data?.driverOpen && <button className="btn-primary" onClick={() => setSyncing(true)} title="기사들이 기사접수 앱(시트)으로 신청하는 동안만 쓰는 버튼">시트 새 신청 가져오기</button>}
          {canWrite && <button className="btn-primary" onClick={() => setProxyOpen(true)} title="#206 — 회계팀이 기사 대신 페이앱·카드·현금영수증·다수배분을 등록합니다(기사 앱과 같은 번호·신청자)">기사 대신 등록</button>}
        </div>
      </div>
      <p className="page-hint">
        기사 앱의 페이앱·카드단말기·현금영수증·다수배분 신청입니다(세금계산서는 「세금계산서」 탭). 칸을 누르고 Ctrl+C 를 누르면 복사, 두 번 누르면 고칩니다 —
        <b> 처리자</b>를 적으면 처리완료, <b>입금일자</b>를 적으면 입금완료. 다수배분 금액은 배분 줄의 합입니다.
        {' '}여기가 정본입니다(10-08 부터 버스인웹 전용 — 옛 기사접수 시트는 읽지도 쓰지도 않습니다).
      </p>
      {data && <DriverSyncLine sync={data.driverSync} open={data.driverOpen} />}
      {error && <ErrorBox message={error} />}
      <div className="flex flex-wrap gap-2 text-sm">
        {data?.totals.filter((t) => t.kind).map((t) => (
          <span key={t.kind} className="rounded-lg bg-canvas px-3 py-1.5">
            {t.kind} {t.n}건 · <b>{money(t.amount)}</b>
            {Number(t.unpaid) > 0 && <span className="text-amber-700"> · 미입금 {money(t.unpaid)}</span>}
          </span>
        ))}
        {grand && <span className="rounded-lg bg-brand-50 px-3 py-1.5 font-semibold">합계 {grand.n}건 · {money(grand.amount)} · 미입금 {money(grand.unpaid)}</span>}
        {data && data.rows.length === 0 && !loading && <span className="text-ink-faint">이 조건의 신청이 없습니다.</span>}
      </div>
      <TopScrollbar target={fitRef} />
      <div ref={fitRef} className="overflow-auto" style={{ maxHeight: fitH }}>
        <table className={`w-full min-w-[1300px] text-xs [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-surface ${anyWidth ? 'table-fixed' : ''}`}>
          {anyWidth && <colgroup>{HEADS.map(([h]) => <col key={h} style={h === '확인' ? { width: prefs.width[h] ?? 44 } : colWidthStyle(prefs, h)} />)}</colgroup>}
          <thead>
            <tr className="border-b text-left text-ink-soft">
              {HEADS.map(([h, k]) => (
                <SortTh key={h} sortKey={k} sort={sort} onSort={onSort} fixed={anyWidth} after={<ResizeHandle onResize={(px) => setWidth(h, px)} />}
                        className={`px-2 py-2 ${h === '금액' || h === '수수료' ? 'text-right' : ''}`}>{h}</SortTh>
              ))}
            </tr>
          </thead>
          <tbody className={`[&_td]:py-1.5 ${loading ? 'opacity-50' : ''} ${anyWidth ? '[&_td]:overflow-hidden [&_td]:text-ellipsis' : ''}`}>{body}</tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center gap-3 text-sm">
          <button className="btn-ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>이전</button>
          <span className="text-ink-soft">{page + 1} / {pages} 쪽 · {page * PAGE + 1}–{Math.min(total, (page + 1) * PAGE)} / {total}건</span>
          <button className="btn-ghost" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>다음</button>
        </div>
      )}

      {editing && (
        <Modal width="max-w-3xl">
          <OrderForm row={editing} onClose={() => setEditing(null)} onSaved={(msg) => { setEditing(null); reload(); if (msg) setNotice(msg); }} onNotified={reload} />
        </Modal>
      )}
      {proxyOpen && (
        <Modal width="max-w-3xl">
          <ProxyRequestForm mode="order" onClose={() => setProxyOpen(false)}
                            onSaved={(no) => { setProxyOpen(false); setNotice(`기사 대신 등록했습니다 — ${no}`); reload(); }} />
        </Modal>
      )}
      {syncing && (
        <Modal width="max-w-3xl">
          <SyncOrders onClose={() => setSyncing(false)} onDone={reload} />
        </Modal>
      )}
    </div>
  );
}

// ─── 고치기 ──────────────────────────────────────────────────────────────────
/** 입력 칸의 금액 글자("118,182원") → 수. 못 읽으면 0. */
const num = (v: string) => Number(v.replace(/[,\s원]/g, '')) || 0;

/** #405·#407 지급금액 — 공제수수료 = 합계 × 수수료율(원 단위 반올림) · 지급금액 = 공급가 − 공제수수료. 서버(driverOrderFin.ORDER_FIN·orderPay)와 같은 표·식. */
const ORDER_FIN: Partial<Record<DriverOrderKind, { perMille: number; account: string; label: string }>> = {
  페이앱: { perMille: 38, account: '[개인]페이앱', label: '3.8%' },
  카드단말기: { perMille: 25, account: '[개인]카드', label: '2.5%' },
};
const orderPay = (perMille: number, supply: number, vat: number) => { const fee = Math.round((supply + vat) * perMille / 1000); return { fee, pay: supply - fee }; };

/** 저장 뒤 알릴 말 — 재무추가 지급 줄을 넣었나·옮겼나·지웠나. */
const finMsg = (no: string, fin: DriverOrderFinResult | undefined) => {
  if (!fin) return null;
  if (fin.note) return `${no} — ${fin.note}`;
  if (fin.summary) return fin.action === 'kept' ? null : `${no} — ${fin.summary}.`;
  if (fin.action === 'added') return `${no} — 재무추가 ${fin.driveMon} 지급 ${Number(fin.amount).toLocaleString()}원을 넣었습니다(NO ${fin.resultDailyId}).`;
  if (fin.action === 'moved') return `${no} — 재무추가 지급 줄을 ${fin.driveMon} ${Number(fin.amount).toLocaleString()}원으로 바꿨습니다(NO ${fin.resultDailyId}).`;
  if (fin.action === 'removed') return `${no} — 재무추가 지급 줄(NO ${fin.resultDailyId})을 지웠습니다.`;
  return null;
};

function OrderForm({ row, onClose, onSaved, onNotified }: { row: DriverOrderRow; onClose: () => void; onSaved: (msg?: string | null) => void; onNotified?: () => void }) {
  const [f, setF] = useState({
    custName: row.cust_name ?? '', bizNumber: row.biz_number ?? '', cashId: row.cash_id ?? '', email: row.email ?? '', phone: row.phone ?? '',
    requestDate: row.request_date ?? '', supply: Number(row.supply).toLocaleString(), vat: Number(row.vat).toLocaleString(),
    memo: row.memo ?? '', issuer: row.issuer ?? '', paidDate: row.paid_date ?? '', settleMon: row.settle_mon ?? '',
    depositAmount: row.deposit_amount ? Number(row.deposit_amount).toLocaleString() : '',
  });
  /** 수수료 계산 기준 — 다수배분은 배분 합(목록 금액), 나머지는 입력 중인 공급가 + 부가세. */
  const rowAmount = row.kind === '다수배분' ? Number(row.amount) : num(f.supply) + num(f.vat);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [lineConfirm, setLineConfirm] = useState<number | null>(null);
  /** #490 기사가 사진을 안 올리고 문자로 보낸 영수증 — 회계가 이 창에서 붙인다(저장 때 함께 · 있던 첨부는 바뀜). */
  const [attach, setAttach] = useState<{ path: string; type: string; name: string } | null>(null);
  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setError(null);
    try { setAttach(await uploadAcctDriverFile({ order: Number(row.order_id) }, file)); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  }
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF((x) => ({ ...x, [k]: e.target.value }));
  const dasu = row.kind === '다수배분';

  const fin = ORDER_FIN[row.kind];
  const cp = orderPay(fin?.perMille ?? 0, num(f.supply), num(f.vat));
  async function run(fn: () => Promise<{ fin?: DriverOrderFinResult } | unknown>) {
    setBusy(true); setError(null);
    try { const r = await fn() as { fin?: DriverOrderFinResult }; onSaved(finMsg(row.order_no, r?.fin)); } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  }
  const save = () => run(() => api.saveDriverOrder({
    id: Number(row.order_id), ...f, requestDate: f.requestDate || null, paidDate: f.paidDate || null, settleMon: f.settleMon || null,
    supply: f.supply.replace(/[,\s원]/g, '') || '0', vat: f.vat.replace(/[,\s원]/g, '') || '0',
    depositAmount: f.depositAmount.replace(/[,\s원]/g, '') || null,
    ...(attach ? { attach } : {}),
  }));
  const field = (label: string, k: keyof typeof f, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label className="space-y-1">{label}<input className="field w-full" value={f[k]} onChange={set(k)} {...props} /></label>
  );

  return (
    <div className="card max-h-[90vh] space-y-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded px-1.5 text-xs ${KIND_STYLE[row.kind]}`}>{row.kind}</span>
        <h2 className="font-mono font-semibold">{row.order_no}</h2><Status r={row} />
        <span className="text-sm text-ink-soft">{row.corp_name} · {dash(row.requester)} · 접수 {dash(row.received_date)}</span>
      </div>
      <fieldset className="grid grid-cols-3 gap-3 text-sm">
        {!dasu && <>{field('거래처명', 'custName')}{field(row.kind === '현금영수증' ? '사업자번호' : '사업자번호', 'bizNumber')}
          {row.kind === '현금영수증' ? field('현금영수증 발급 번호', 'cashId') : field('이메일', 'email')}</>}
        {field('요청일자', 'requestDate', { type: 'date' })}
        {!dasu && <>{field('공급가', 'supply', { className: 'field w-full text-right' })}{field('부가세', 'vat', { className: 'field w-full text-right' })}
          <label className="space-y-1">합계<input className="field w-full bg-canvas text-right" readOnly tabIndex={-1}
            value={(num(f.supply) + num(f.vat)).toLocaleString()} /></label></>}
        {field('연락처', 'phone')}{field('정산년월', 'settleMon', { type: 'month' })}
        <label className="col-span-3 space-y-1">비고<input className="field w-full" value={f.memo} onChange={set('memo')} /></label>
        {field('처리자 → 처리완료', 'issuer')}{field('입금일자 → 입금완료', 'paidDate', { type: 'date' })}
        {/* #208 — 실제 들어온 금액(카드 수수료를 뗀 뒤). 목록 「수수료」 = 금액 − 이 값. */}
        <label className="space-y-1">입금금액
          <input className="field w-full text-right" value={f.depositAmount} onChange={set('depositAmount')} placeholder="실제 입금된 금액" />
          {f.depositAmount.trim() && <span className="block text-[11px] text-ink-soft">수수료 {(rowAmount - num(f.depositAmount)).toLocaleString()}원</span>}
        </label>
        {/* #405·#407 지급금액 = 공급가 − 합계 × 수수료율 · 정산년월을 넣으면 그 달 재무추가 지급으로 자동 입력 */}
        {fin && (
          <label className="space-y-1" title={`공급가 − 합계 × ${fin.label} · 정산년월을 넣고 저장하면 그 달 재무추가 지급(${fin.account})으로 자동으로 들어갑니다`}>지급금액
            <input className="field w-full bg-canvas text-right font-semibold" readOnly tabIndex={-1} value={cp.pay.toLocaleString()} />
            <span className="block text-[11px] text-ink-soft">공제수수료 {cp.fee.toLocaleString()}원</span>
          </label>
        )}
      </fieldset>
      {fin && (
        <p className="text-xs text-ink-soft">
          {row.fin
            ? <>재무추가 <b>{row.fin.drive_mon}</b> 지급 {money(row.fin.amount)}원 들어감(NO {row.fin.result_daily_id}) · 정산년월을 바꾸면 옮기고, 비우거나 취소하면 지웁니다.</>
            : <>정산년월을 넣고 저장하면 그 달 재무추가에 지급금액이 <b>{fin.account}</b>로 들어갑니다(비고: 요청일자 거래처명 비고).</>}
        </p>
      )}
      {/* #516 다수배분 → 정산년월을 넣고 저장하면 배분하는 기사 공제 + 받는 기사 지급 */}
      {dasu && (
        <p className="text-xs text-ink-soft">
          {row.dasu_fin
            ? <>재무추가 <b>{row.dasu_fin.drive_mon}</b> 공제 {row.dasu_fin.deduct}줄 · 지급 {row.dasu_fin.pay}줄 들어감 · 정산년월을 바꾸면 옮기고, 비우거나 취소(줄 취소 포함)하면 지웁니다.</>
            : <>정산년월을 넣고 저장하면 그 달 재무추가에 배분하는 기사는 공제(<b>[개인]내부운행료</b> · 외부배분은 <b>[개인]외부운행료</b>), 받는 기사는 지급(<b>[개인]배분 운행</b>)으로 줄마다 들어갑니다.</>}
        </p>
      )}
      {row.lines.some((l) => l.dist_type !== '배분없음') && (
        <div className="rounded border">
          <table className="w-full text-xs">
            <thead><tr className="border-b text-left text-ink-soft"><th className="px-2 py-1">#</th><th>구분</th><th>받는 사람</th><th className="text-right">금액</th><th>실행일자</th><th>처리자</th><th>입금일자</th><th /><th /></tr></thead>
            <tbody>
              {row.lines.map((l) => (
                <tr key={l.line_no} className={`border-b ${l.cancelled ? 'text-ink-faint line-through' : ''}`}>
                  <td className="px-2 py-1">{l.line_no}</td><td>{dash(l.dist_type)}</td><td>{dash(l.dist_name)}</td>
                  <td className="text-right">{money(l.amount)}</td><td>{dash(l.exec_date)}</td><td>{l.cancelled ? '신청취소' : dash(l.issuer)}</td><td>{dash(l.paid_date)}</td>
                  <td>{l.attach_url && <a className="text-brand-700 underline" href={l.attach_url} target="_blank" rel="noopener noreferrer">첨부</a>}</td>
                  {/* #501 다수배분 줄마다 취소·되살리기(신청 전체 취소는 아래 「취소 처리」) */}
                  <td className="whitespace-nowrap pr-2 text-right">
                    {!row.cancel_state && (lineConfirm === l.line_no
                      ? <button type="button" className="text-red-600 underline" disabled={busy}
                                onClick={() => run(() => api.cancelDriverOrder({ id: Number(row.order_id), lineNo: l.line_no, undo: l.cancelled }))}>
                          {l.cancelled ? '정말 되살리기' : '정말 취소'}</button>
                      : <button type="button" className={l.cancelled ? 'text-ink-soft underline' : 'text-red-600 underline'}
                                onClick={() => setLineConfirm(l.line_no)}>{l.cancelled ? '되살리기' : '이 줄 취소'}</button>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-2 py-1 text-[11px] text-ink-faint">처리자·입금일자를 저장하면 취소 안 된 줄 전부에 같이 적힙니다(시트와 같은 방식).</p>
        </div>
      )}
      {(row.kind === '페이앱' || row.kind === '현금영수증') && <DriverNoticePanel target="order" id={Number(row.order_id)} onSent={onNotified} />}
      {/* #337 — 기사 앱에서 올린 첨부(카드 사진 등)도 세금계산서 창처럼 바로 연다. 시트 시절 건은 드라이브 링크. */}
      {(row.attach_name || row.attach_url) && (
        <p className="text-sm">첨부{' '}
          {row.attach_name
            ? <button type="button" className="text-brand-700 underline" onClick={() => void (async () => {
                try { const url = await fetchDriverOrderFileUrl(Number(row.order_id)); window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60_000); }
                catch (e) { setError(errMsg(e)); }
              })()}>📎 {row.attach_name}</button>
            : <a className="text-brand-700 underline" href={row.attach_url!} target="_blank" rel="noopener noreferrer">📎 구글 드라이브에서 보기</a>}
        </p>
      )}
      {row.from_app && (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          {attach ? (
            <><span className="truncate">📎 {attach.name} — 저장하면 {row.attach_name || row.attach_url ? '이 파일로 바뀝니다' : '붙습니다'}</span>
              <button type="button" className="text-xs underline" onClick={() => setAttach(null)}>빼기</button></>
          ) : (
            <label className="flex items-center gap-2">{row.attach_name || row.attach_url ? '첨부 바꾸기' : '파일 올리기'}
              <input type="file" accept="image/*,application/pdf" disabled={busy} onChange={onFile} className="block text-xs" title="사진(JPG·PNG) 또는 PDF" /></label>
          )}
        </div>
      )}
      {error && <ErrorBox message={error} />}
      <div className="flex flex-wrap gap-2">
        <button className="btn-primary" onClick={save} disabled={busy}>{busy ? '저장 중…' : '저장'}</button>
        <button className="btn-ghost" onClick={onClose}>닫기</button>
        {confirm
          ? <button className="btn-ghost ml-auto text-red-600" disabled={busy} onClick={() => run(() => api.cancelDriverOrder({ id: Number(row.order_id), undo: Boolean(row.cancel_state) }))}>
              {row.cancel_state ? '정말 되살리기' : '정말 취소'}</button>
          : <button className="btn-ghost ml-auto text-red-600" onClick={() => setConfirm(true)}>{row.cancel_state ? '취소 되돌리기' : '취소 처리'}</button>}
      </div>
    </div>
  );
}

// ─── 시트 새 신청 가져오기(전환기) ───────────────────────────────────────────
function SyncOrders({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [preview, setPreview] = useState<DriverOrderImportTab[] | null>(null);
  const [result, setResult] = useState<{ tabs: DriverOrderImportTab[]; writeBack: { asked: number; updated: number; missing: string[] } | null } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function look() {
    setBusy('시트를 읽는 중… (20개 탭, 1분쯤)'); setError(null);
    try { setPreview((await api.syncDriverOrders({})).tabs); } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }
  async function apply() {
    setBusy('적용하는 중…'); setError(null);
    try { const r = await api.syncDriverOrders({ commit: true }); setResult(r); onDone(); } catch (e) { setError(errMsg(e)); } finally { setBusy(null); }
  }
  useEffect(() => { void look(); }, []);
  const shown = result?.tabs ?? preview;
  const sum = (k: 'added' | 'cancelled' | 'unchanged') => (shown ?? []).reduce((s, t) => s + t[k], 0);
  const conflicts = (shown ?? []).flatMap((t) => t.conflicts);

  return (
    <div className="card max-h-[90vh] space-y-3 overflow-y-auto p-5 text-sm">
      <h2 className="font-semibold">시트 새 신청 가져오기 — 페이앱·카드단말기·현금영수증·다수배분</h2>
      <p className="text-ink-soft">
        기사접수 시트에서 <b>새 번호</b>와 <b>기사가 한 취소</b>만 들여옵니다. 이미 있는 번호는 건드리지 않습니다(여기서 고친 값이 이깁니다).
        적용하면 여기서 고친 처리자·입금일자를 시트에도 적습니다(기사 조회 화면용).
      </p>
      {busy && <p className="text-ink-soft">{busy}</p>}
      {error && <ErrorBox message={error} />}
      {shown && (
        <>
          <p className="font-medium">{result ? '적용했습니다' : '미리보기'}: 새로 {sum('added')} · 취소 들여옴 {sum('cancelled')} · 그대로 {sum('unchanged')}
            {conflicts.length > 0 && <span className="text-red-700"> · 충돌 {conflicts.length}</span>}</p>
          <div className="max-h-72 overflow-auto rounded border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white"><tr className="border-b text-left text-ink-soft"><th className="px-2 py-1">탭</th><th>번호</th><th>새로</th><th>취소</th><th>그대로</th><th>건너뜀·충돌</th></tr></thead>
              <tbody>
                {shown.map((t) => (
                  <tr key={t.tab} className="border-b">
                    <td className="px-2 py-1">{t.tab}</td><td>{t.numbers}</td><td className={t.added ? 'font-semibold text-brand-700' : ''}>{t.added}</td>
                    <td>{t.cancelled}</td><td>{t.unchanged}</td>
                    <td className="text-red-700">{[...t.skipped, ...t.conflicts].slice(0, 3).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result?.writeBack && <p className="text-ink-soft">시트에 적음: {result.writeBack.updated}건 / 보낸 {result.writeBack.asked}건
            {result.writeBack.missing.length > 0 && ` · 시트에서 못 찾음 ${result.writeBack.missing.length}건`}</p>}
        </>
      )}
      <div className="flex gap-2">
        {!result && <button className="btn-primary" onClick={apply} disabled={!!busy || !preview}>적용</button>}
        <button className="btn-ghost" onClick={onClose}>닫기</button>
      </div>
    </div>
  );
}
